import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoClose } from 'react-icons/io5';
import { FaArrowRight } from 'react-icons/fa';
import { HiOutlineDocumentText, HiOutlineBriefcase, HiOutlineRocketLaunch, HiOutlineCreditCard } from 'react-icons/hi2';
import { BiTimeFive } from 'react-icons/bi';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { storageGet, storageSet } from '@/utils/safe-storage';
import '@/styles/coaching.css';

const STORAGE_KEY = 'coaching-announcement-seen';
const DELAY_MS = 12000;

export const CoachingAnnouncementDialog = () => {
    const [show, setShow] = useState(false);
    const { locale } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();

    useEffect(() => {
        if (storageGet('local', STORAGE_KEY) === 'true') return;
        const timer = setTimeout(() => setShow(true), DELAY_MS);
        return () => clearTimeout(timer);
    }, []);

    const dismiss = useCallback(() => {
        setShow(false);
        storageSet('local', STORAGE_KEY, 'true');
    }, []);

    const goToCoaching = useCallback(() => {
        storageSet('local', STORAGE_KEY, 'true');
        navigate(`/${locale}/coaching`);
    }, [locale, navigate]);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') dismiss();
        };

        if (show) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [show, dismiss]);

    const features = [
        { icon: <HiOutlineDocumentText className="w-5 h-5" />, key: 'announcement.coaching.feature1' },
        { icon: <HiOutlineBriefcase className="w-5 h-5" />, key: 'announcement.coaching.feature2' },
        { icon: <HiOutlineRocketLaunch className="w-5 h-5" />, key: 'announcement.coaching.feature3' },
        { icon: <HiOutlineCreditCard className="w-5 h-5" />, key: 'announcement.coaching.feature4' },
    ];

    const modalContent = (
        <AnimatePresence>
            {show && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="coaching-announcement-backdrop"
                        onClick={dismiss}
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{
                            duration: 0.3,
                            ease: 'easeOut',
                            type: 'spring',
                            stiffness: 300,
                            damping: 30,
                        }}
                        className="coaching-announcement-card"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={dismiss}
                            className="coaching-announcement-close"
                            aria-label="Close dialog"
                        >
                            <IoClose aria-hidden="true" />
                        </button>

                        <div className="text-left">
                            <span className="title-heading">{t('topNav.coaching')}</span>
                            <h2 className="coaching-announcement-title">
                                {t('announcement.coaching.title')}
                            </h2>
                            <p className="coaching-announcement-subtitle">
                                {t('announcement.coaching.subtitle')}
                            </p>

                            <div className="coaching-announcement-features">
                                {features.map((feature, index) => (
                                    <div key={index} className="coaching-announcement-feature">
                                        <span className="coaching-announcement-feature-icon">
                                            {feature.icon}
                                        </span>
                                        <span>{t(feature.key)}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="coaching-announcement-note">
                                <BiTimeFive aria-hidden="true" />
                                <span>{t('announcement.coaching.limitedOffer')}</span>
                            </div>

                            <button
                                onClick={goToCoaching}
                                className="button button-dark"
                            >
                                {t('announcement.coaching.cta')}
                                <FaArrowRight aria-hidden="true" />
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );

    return createPortal(modalContent, document.body);
};
